import type { Shape } from "@/api/client";

/** A page's size in the units the SVG is drawn in; points are scaled by it. */
export interface PageSize {
  w: number;
  h: number;
}

type Point = readonly [number, number];

const at = (p: Point, page: PageSize): Point => [p[0] * page.w, p[1] * page.h];

/** The two barbs of an arrowhead at `tip`, coming from `from`, `size` long. */
export function arrowHead(from: Point, tip: Point, size: number): [Point, Point] {
  const angle = Math.atan2(tip[1] - from[1], tip[0] - from[0]);
  const spread = Math.PI / 7;
  return [
    [tip[0] - size * Math.cos(angle - spread), tip[1] - size * Math.sin(angle - spread)],
    [tip[0] - size * Math.cos(angle + spread), tip[1] - size * Math.sin(angle + spread)],
  ];
}

interface Props {
  shape: Shape;
  page: PageSize;
  /** The width of the visible window, in the same units, so marks read the same at any zoom. */
  viewWidth: number;
  selected?: boolean;
  /** Draw an invisible copy this many pixels wide, as the target for picking the mark. */
  hitWidth?: number;
}

/**
 * One estimator mark drawn over a sheet. Strokes do not scale with zoom; text and
 * arrowheads are sized to the visible window, so they stay legible at any zoom.
 */
export function ShapeView({ shape, page, viewWidth, selected = false, hitWidth }: Props) {
  const points = shape.points.map((p) => at(p, page));
  const stroke = {
    stroke: hitWidth ? "transparent" : shape.colour,
    strokeWidth: hitWidth ?? (selected ? 3.5 : 2.5),
    pointerEvents: hitWidth ? ("stroke" as const) : undefined,
    vectorEffect: "non-scaling-stroke" as const,
    fill: "none",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const first = points[0];
  if (!first) {
    return null;
  }
  const last = points[points.length - 1] ?? first;
  switch (shape.kind) {
    case "arrow": {
      const [left, right] = arrowHead(first, last, viewWidth / 70);
      return (
        <g data-shape="arrow">
          <line x1={first[0]} y1={first[1]} x2={last[0]} y2={last[1]} {...stroke} />
          <polyline points={`${left.join(",")} ${last.join(",")} ${right.join(",")}`} {...stroke} />
        </g>
      );
    }
    case "rect":
      return (
        <rect
          data-shape="rect"
          x={Math.min(first[0], last[0])}
          y={Math.min(first[1], last[1])}
          width={Math.abs(last[0] - first[0])}
          height={Math.abs(last[1] - first[1])}
          {...stroke}
        />
      );
    case "ellipse":
      return (
        <ellipse
          data-shape="ellipse"
          cx={(first[0] + last[0]) / 2}
          cy={(first[1] + last[1]) / 2}
          rx={Math.abs(last[0] - first[0]) / 2}
          ry={Math.abs(last[1] - first[1]) / 2}
          {...stroke}
        />
      );
    case "freehand":
      return (
        <polyline
          data-shape="freehand"
          points={points.map((p) => p.join(",")).join(" ")}
          {...stroke}
        />
      );
    case "text":
      return (
        <text
          data-shape="text"
          x={first[0]}
          y={first[1]}
          fill={shape.colour}
          fontSize={viewWidth / 60}
          fontWeight={600}
          stroke="white"
          strokeWidth={viewWidth / 600}
          paintOrder="stroke"
          dominantBaseline="hanging"
        >
          {shape.text}
        </text>
      );
  }
}
